import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "truecost-theme"
const THEMES = ["light", "dark"]

export default class extends Controller {
  static targets = ["button", "label", "lightIcon", "darkIcon"]
  static values = {
    lightLabel: String,
    darkLabel: String
  }

  connect() {
    this.systemPreference = this.resolveSystemPreference()
    this.systemThemeChanged = () => {
      if (!this.savedTheme()) {
        this.applyTheme(this.systemTheme())
      }
    }

    this.systemPreference?.addEventListener("change", this.systemThemeChanged)
    this.applyTheme(this.savedTheme() || this.systemTheme())
  }

  disconnect() {
    this.systemPreference?.removeEventListener("change", this.systemThemeChanged)
  }

  toggle() {
    const nextTheme = this.currentTheme() === "dark" ? "light" : "dark"
    this.storeTheme(nextTheme)
    this.applyTheme(nextTheme)
  }

  applyTheme(theme) {
    document.documentElement.dataset.theme = theme
    this.updateButton(theme)
  }

  updateButton(theme) {
    const isDark = theme === "dark"
    const label = isDark ? this.darkLabelValue : this.lightLabelValue

    this.labelTarget.textContent = label
    this.buttonTarget.title = label
    this.buttonTarget.setAttribute("aria-pressed", String(isDark))
    this.lightIconTarget.classList.toggle("hidden", isDark)
    this.darkIconTarget.classList.toggle("hidden", !isDark)
  }

  currentTheme() {
    return THEMES.includes(document.documentElement.dataset.theme) ? document.documentElement.dataset.theme : this.systemTheme()
  }

  savedTheme() {
    try {
      const theme = localStorage.getItem(STORAGE_KEY)
      return THEMES.includes(theme) ? theme : null
    } catch (error) {
      return null
    }
  }

  storeTheme(theme) {
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch (error) {
      // Browsers can block localStorage; keep the in-memory theme applied for this page.
    }
  }

  systemTheme() {
    return this.systemPreference?.matches ? "dark" : "light"
  }

  resolveSystemPreference() {
    if (typeof window.matchMedia !== "function") return null

    return window.matchMedia("(prefers-color-scheme: dark)")
  }
}
